import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BayclubsAlertService } from '@bayclubs/alert';
import { RefundService } from '@bayclubs/payments-processor-office-api';
import { PaymentProcessorCloseReason, PaymentProcessorOfficeLauncherService, PaymentsProcessorOfficeLauncherModel } from '@bayclubs/payments-processor-office-launcher';
import { api, ConfirmationService, ErrorTranslationService, ModalService } from '@bayclubs/utils';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, of, Subscription, switchMap, take, tap } from 'rxjs';
import { camps } from '../../../models/models.generated';
import { CartApiService } from '../../../services/api/cart-api.service';
import { PaymentApiService } from '../../../services/api/payment-api.service';
import { PaymentProcessorIntegrationService } from '../../../services/payment-processor-integration.service';
import { PaymentsProcessorLaunchAuthService } from '../../../services/payments-processor-launch-auth.service';
import { SessionsWeek, SessionsWeeksService } from '../session-weeks.service';
import { PurchaseEditEventService } from './purchase-edit-event.service';
import { PurchaseEditRefundReasonModalComponent } from './purchase-edit-refund-reason-modal/purchase-edit-refund-reason-modal.component';
import { campsenum } from '../../../models/enums.generated';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-purchase-edit',
    templateUrl: './purchase-edit.component.html',
    styleUrls: ['./purchase-edit.component.scss'],
    standalone: false
})
export class PurchaseEditComponent implements OnInit, OnDestroy {
    private view: PaymentEditViewType = PaymentEditViewType.Edit;
    private purchaseSetId: number;
    private subscription = new Subscription();

    public cart: camps.OfficeCartInfo;
    public cartSummary: camps.OfficeCartSummaryInfo;
    public waitingForPaymentProcessorResponse = false;

    public weeks: SessionsWeek[];

    constructor(
        private activatedRoute: ActivatedRoute,
        private cartApi: CartApiService,
        private purchaseEditEvent: PurchaseEditEventService,
        private weekService: SessionsWeeksService,
        private translate: TranslateService,
        private confirmation: ConfirmationService,
        private alert: BayclubsAlertService,
        private paymentApi: PaymentApiService,
        private router: Router,
        private location: Location,
        private errorTranslate: ErrorTranslationService,
        private modal: ModalService,
        private paymentProcessorIntegrationService: PaymentProcessorIntegrationService,
        private refundService: RefundService,
        private launchAuth: PaymentsProcessorLaunchAuthService,
        private paymentProcessorOfficeLauncherService: PaymentProcessorOfficeLauncherService
    ) {
    }
    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    ngOnInit() {
        this.activatedRoute.params.subscribe(params => {
            if (params.purchaseSetId) {
                this.purchaseSetId = params.purchaseSetId;
                this.loadCart(params.purchaseSetId);
            }
        });

        this.purchaseEditEvent.cartChanged.subscribe((cart: camps.OfficeCartInfo) => {
            this.cart = cart;

            this.buildWeeks();
        });
    }

    private loadCart(purchaseSetId): void {
        this.cartApi.get(purchaseSetId).subscribe(
            (response: api.OperationResultValue<camps.OfficeCartInfo>) => {
                this.cart = response.value;

                this.buildWeeks();
            },
            error => {
                console.log(error);
            },
            () => {
            }
        );
    }

    private loadSummary(): void {
        this.cartApi.getSummary(this.purchaseSetId).subscribe(
            (response: api.OperationResultValue<camps.OfficeCartSummaryInfo>) => {
                if (response.isSuccess) {
                    this.cartSummary = response.value;
                    this.view = PaymentEditViewType.Summary;
                } else {
                    this.errorTranslate.translateValidationNotifications(response.validationNotification);
                    this.alert.showError(response.validationNotification.errors[0].message);
                }
            },
            error => {
                console.log(error);
            },
            () => {
            }
        );
    }

    public onCancel(): void {
        this.router.navigate(['purchases/' + this.cart.finalizedPurchase.purchaseId + '/details']);
    }

    public onBack() {
        this.view = PaymentEditViewType.Edit;
    }

    public onNext(): void {
        if (this.isEdit) {
            this.loadSummary();
        }
    }

    public get isSummary() {
        return this.view == PaymentEditViewType.Summary;
    }

    public get isEdit() {
        return this.view == PaymentEditViewType.Edit;
    }

    private buildWeeks() {
        this.weeks = this.weekService.createSessionWeeks(this.cart.sessions);
    }

    public get showFinalizeButton() {
        return this.isSummary;
    }

    public onFinalize() {
        if (this.cartSummary.totalAmount >= 0) {
            if (this.cartSummary.isPaymentProcessorPurchase && this.cartSummary?.summary?.valueToBePaidOrReturned > 0) {
                this.performPaymentProcessorRefund();
            } else {
                this.performRefund();
            }
        }
    }

    private performPaymentProcessorRefund() {
        this.subscription.add(
            this.modal.create(PurchaseEditRefundReasonModalComponent, {
                purchase: this.cart.finalizedPurchase
            }).pipe(
                take(1),
                filter((reasonResult: PurchaseEditRefundReasonModalComponent) => !!reasonResult?.value),
                switchMap(result =>
                    this.cartApi.setRefundReason({
                        refundInfo: {
                            refundReason: result.value.reason,
                            comment: result.value.comment,
                        },
                        purchaseSetId: this.cart.purchaseSetId,
                        currentPurchaseId: this.cart.purchaseId
                    } as camps.PartialRefundSetReasonRequest)
                ),
                switchMap((refundReasonResponse: api.OperationResult) => {
                    if (!refundReasonResponse.isSuccess) {
                        this.errorTranslate.translateValidationNotifications(refundReasonResponse.validationNotification);
                        this.alert.showError(refundReasonResponse.validationNotification.errors[0]?.message ?? refundReasonResponse.errorMessage);
                        return of();
                    }

                    const request = this.paymentProcessorIntegrationService.mapToPaymentProcessorRefund(this.cartSummary);
                    this.waitingForPaymentProcessorResponse = true;
                    return this.refundService.api10RefundsPost(request);
                }),
                switchMap(postRefundResponse => {
                    const ppRefundRequest: PaymentsProcessorOfficeLauncherModel = {
                        accessToken: this.launchAuth.accessToken,
                        authMode: this.launchAuth.authMode,
                        identityHintProvider: this.launchAuth.identityHintProvider,
                        refundId: postRefundResponse.refundScheduleId,
                        cancelOnModalClose: true,
                        correlationId: postRefundResponse.correlationId,
                        officeAppUrl: postRefundResponse.officeAppUrl,
                        parentOrigin: window.location.origin,
                        theme: 'dark',
                        transactionId: null
                    };
                    return this.paymentProcessorOfficeLauncherService.open(ppRefundRequest).pipe(
                        switchMap(paymentsProcessorResult => {
                            console.log('Payment Processor Refund Result:', paymentsProcessorResult);
                            if (paymentsProcessorResult) {

                                return this.paymentProcessorIntegrationService
                                    .checkTransactionStatus(this.cartSummary.purchaseId)
                                    .pipe(
                                        tap(waiting => {
                                            if (!waiting) {
                                                this.waitingForPaymentProcessorResponse = false;
                                                this.loadCart(this.purchaseSetId);
                                                this.onBack();
                                            }
                                        })
                                    );
                            } else {
                                return of();
                            }
                        })
                    );
                }),
                catchError((error: HttpErrorResponse) => {
                    this.alert.showError(error?.error?.Errors[0]?.Message ?? error.message);
                    this.waitingForPaymentProcessorResponse = false;
                    return of();
                })
            ).subscribe()
        );
    }

    private performRefund() {

        this.subscription.add(
            this.modal.create(PurchaseEditRefundReasonModalComponent, {
                purchase: this.cart.finalizedPurchase
            }).pipe(
                take(1),
                filter((result: PurchaseEditRefundReasonModalComponent) => !!result?.value),
                switchMap(result => {
                    return this.paymentApi
                        .refund(this.cart.finalizedPurchase.purchaseSetId, {
                            refundReason: result.value.reason,
                            comment: result.value.comment,
                            purchaseSetId: this.cart.purchaseSetId
                        } as camps.SetRefundReasonRequest)
                }),
                switchMap((response) => {
                    if (response.isSuccess) {
                        this.alert.showOperationSuccess();

                        this.router.navigate(['purchases/' + this.cart.purchaseId + '/details']);
                    } else {
                        this.errorTranslate.translateValidationNotifications(response.validationNotification);
                        this.alert.showError(response.validationNotification.errors[0].message);
                    }
                    return of();
                }),
                catchError(error => {
                    this.alert.showError(error.message);
                    return of();
                })
            ).subscribe()
        );
    }

    public get isAnyRemoved(): boolean {
        if (!this.cart) {
            return false;
        }

        let removedSessions = this.cart.sessions.filter(x => x.isRemoved == true);

        return removedSessions.length > 0
            || this.cart.sessions.some(x => x.removedOptions.length > 0);
    }
}

enum PaymentEditViewType {
    Edit,
    Summary
}
